<script setup lang="ts">
// Note:
// Seller 是套用 Swiper 5 版，
// CMS-SDK 是套用 Swiper 6 版，
// 所以要注意 Event 會有些許的不一樣。

// Import Swiper styles
import 'swiper/swiper-bundle.min.css';

// import Swiper core and required modules
import SwiperCore, { Navigation, Pagination } from 'swiper';
// Import Swiper Vue.js components
import { Swiper } from 'swiper/vue';
import { computed, nextTick, onMounted, ref, watch } from 'vue';

import { DEVICE_BREAKPOINT, deviceBreakpoint } from '@/store/breakpoint';

// install Swiper modules
SwiperCore.use([Navigation, Pagination]);

const emit = defineEmits(['slideChange', 'slidesLengthChange']);

const LIMIT_COUNT = 4; // 不同數量，呈現 UI 的不同
const SPACE_BETWEEN_MOBILE = 170;
const SPACE_BETWEEN_PC = 20;

let swiperInstance: any = null;
const isSwiperWrapperCentered = ref(false);

// Note: 在 Seller 與 CMS SDK 有判斷 RWD 方式不同，需要微調。
const isMobile = computed(() => {
  return deviceBreakpoint.value === DEVICE_BREAKPOINT.MOBILE;
});

// Note:
// 小於三張時，特別客制讓 Slide 置中排版，
// 為了不讓三個以下的 Slide 間隔太大。
const calcSwiperClass = computed(() => {
  return {
    'swiper-wrapper-centered': isSwiperWrapperCentered.value,
  };
});

const setSwiperThemeColor = () => {
  document.documentElement.style
    .setProperty('--swiper-theme-color', 'black');
};

const updateSwiperSetting = async () => {
  if (!swiperInstance) { return; }

  const swiper = swiperInstance;
  const slidesCount = swiper?.slides?.length;

  if (isMobile.value) {
    swiper.params.slidesPerView = 2;
    swiper.params.spaceBetween = SPACE_BETWEEN_MOBILE;
    swiper.params.centeredSlides = true;
    isSwiperWrapperCentered.value = false;
  } else if (slidesCount < LIMIT_COUNT) {
    swiper.params.slidesPerView = LIMIT_COUNT;
    swiper.params.spaceBetween = 0;
    swiper.params.centeredSlides = false;
    isSwiperWrapperCentered.value = true;
  } else {
    swiper.params.slidesPerView = LIMIT_COUNT;
    swiper.params.spaceBetween = SPACE_BETWEEN_PC;
    swiper.params.centeredSlides = true;
    isSwiperWrapperCentered.value = false;
  }

  // 刷新 Swiper 設定值
  swiper.update();

  // 當 4 張以上時，讓落點在第 2 張，排版較好看
  if (!isMobile.value && slidesCount >= LIMIT_COUNT) {
    await nextTick();
    swiper.slideTo(1);
  }
};
watch(isMobile, () => updateSwiperSetting());

// 監聽 Swiper 事件
const handleBeforeInit = (swiper: any) => {
  swiperInstance = swiper;
};
const handleSlideChange = (swiper: any) => {
  emit('slideChange', swiper?.activeIndex ?? 0);
};
const handleSlidesLengthChange = (swiper: any) => {
  updateSwiperSetting();
  emit('slidesLengthChange', swiper?.slides?.length ?? 0);
};

onMounted(() => {
  setSwiperThemeColor();
});

const modules = [Navigation, Pagination];
</script>

<template>
<div class="module-carousel-swiper">
  <swiper
    :modules="modules"
    :autoHeight="true"
    :watchOverflow="true"
    :resizeObserver="true"
    :navigation="true"
    :pagination="{ clickable: true }"
    :class="calcSwiperClass"
    @beforeInit="handleBeforeInit"
    @slideChange="handleSlideChange"
    @slidesLengthChange="handleSlidesLengthChange"
  >
    <slot :swiperRef="swiperInstance"></slot>
  </swiper>
</div>
</template>
<style lang="scss" scoped>
// Note:

// Seller 是套用 Swiper 5 版，

// CMS-SDK 是套用 Swiper 6 版，

// 所以要注意 CSS 會有些許的不一樣。

$desktop-fixed-width: 1075px;
$slide-width: 256px;

// ref:https://github.com/ag-grid/ag-grid/issues/4543#issuecomment-868877027
$slide-width-half: $slide-width * 0.5;
$navigation-positioning-displacement: 26px;

.module-carousel-swiper {
  .swiper-container {
    @apply py-11;
  }

  :deep(.swiper-wrapper-centered .swiper-wrapper) {
    @apply justify-center;
  }

  :deep(.swiper-container) {
    @apply h-auto;
  }

  :deep(.swiper-slide) {
    @apply flex justify-center items-center;
  }

  .swiper-pagination-bullets {
    @apply w-full left-0 bottom-2;
  }

  :deep(.swiper-pagination-bullet) {
    @apply mx-1 my-0 border-2 border-gray-900 bg-transparent;
  }

  :deep(.swiper-pagination-bullet-active) {
    @apply border-black bg-black;
  }

  :deep(.swiper-button-prev),
  :deep(.swiper-button-next) {
    @apply w-9 h-9 rounded-full bg-none;

    top: 50% !important;

    &:hover {
      @apply bg-black bg-opacity-10;
    }

    &::after {
      @apply w-full h-full transition-none transform-none;

      content: "";
    }
  }

  :deep(.swiper-button-next) {
    right: calc(50% - #{$slide-width-half} - #{$navigation-positioning-displacement}) !important;

    &::after {
      background-image: url('@/assets/icons/arrow-circle-right-light.svg');
      background-size: cover;
    }
  }

  :deep(.swiper-button-prev) {
    left: calc(50% - #{$slide-width-half} - #{$navigation-positioning-displacement}) !important;

    &::after {
      background-image: url('@/assets/icons/arrow-circle-left-light.svg');
      background-size: cover;
    }
  }

  :deep(.swiper-button-disabled) {
    @apply opacity-0;
  }
}
</style>
